import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmationPopupComponent } from '../../../shared/confirmation-popup/confirmation-popup.component';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { VendorQuoteDataService } from '../../../shared/vendor-quotes/vendor-quote-data.service';
import { ExtractedField, ExtractedLine, VendorQuoteDraft } from '../../../shared/vendor-quotes/vendor-quote-flow.types';
import { duplicateQuoteDialogData } from '../../../shared/vendor-quotes/vendor-quote-flow.util';

/**
 * Review opened as a dialog from the PO's Upload Vendor Quote. The draft is handed in, there is no
 * bid or scope, edits stay on the draft, and "Review Changes" closes with it so the PO can go on to
 * Compare. Without this data the component is the bid's routed page, unchanged.
 */
export interface VendorQuoteReviewDialogData {
  draft: VendorQuoteDraft;
  /** What the quote is for, shown in the header, e.g. "PO line Product-001". */
  forLabel: string;
}

export interface VendorQuoteReviewDialogResult {
  draft: VendorQuoteDraft;
}

/** Position of the Vendor Quotes tab on the Bid detail page (used for `?tab=`). */
const BID_VENDOR_QUOTES_TAB = 6;

interface ScopeOption {
  /** scopes.id — what the pivot stores; the label is for display only. */
  id: number;
  label: string;
  suggested: boolean;
  /** How many matched lines' products are in this scope (0 when none). */
  matchCount: number;
}

/**
 * Review Extracted Quote (Upload Vendor Quote flow, mockup D1–D10).
 * The draft comes from VendorQuoteDataService (mock until the backend exists — API-SWAP markers there);
 * the bid and its scopes come from the real APIs.
 */
@Component({
  selector: 'app-vendor-quote-review',
  templateUrl: './vendor-quote-review.component.html',
  styleUrls: ['./vendor-quote-review.component.scss'],
  standalone: false
})
export class VendorQuoteReviewComponent implements OnInit, OnDestroy {
  bidId!: number;
  draftId!: number;
  draft: VendorQuoteDraft | undefined;
  bid: { bid_number?: string; name?: string } | null = null;
  isLoading = true;
  editing = false;
  isSaving = false;
  scopeOptions: ScopeOption[] = [];
  scopeError = false;
  /** Scopes this screen selected by itself from the matched items (shown with a "calculated" note). */
  autoScopeIds: number[] = [];
  /** Matched products' scopes that are NOT on this bid, so can't be chosen: label → how many items. */
  offBidScopes: Array<{ label: string; count: number }> = [];
  private scopesLoaded = false;
  private scopeSuggestionDone = false;
  previewUrl: SafeResourceUrl | null = null;
  imageUrl: string | null = null;

  /** Rich tooltip for NEW PRODUCT (D7): rendered fixed, next to the badge. */
  newProductTip: { line: ExtractedLine; x: number; y: number } | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private backendApiService: BackendApiService,
    private toasterService: ToasterService,
    private vendorQuoteDataService: VendorQuoteDataService,
    private dialog: MatDialog,
    @Optional() private dialogRef: MatDialogRef<VendorQuoteReviewComponent, VendorQuoteReviewDialogResult | undefined> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: VendorQuoteReviewDialogData | null,
  ) {}

  /** Opened as a dialog from the PO with a draft handed in, rather than as the bid's page. */
  get inDialog(): boolean {
    return !!this.dialogRef && !!this.dialogData?.draft;
  }

  ngOnInit(): void {
    if (this.inDialog) {
      this.draft = this.dialogData!.draft;
      this.isLoading = false;
      const file = this.draft.file;
      if (file.url && file.mime === 'application/pdf') {
        this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${file.url}#toolbar=0&navpanes=0`);
      } else if (file.url && file.mime.startsWith('image/')) {
        this.imageUrl = file.url;
      }
      return;
    }
    this.bidId = Number(this.route.snapshot.paramMap.get('id'));
    this.draftId = Number(this.route.snapshot.paramMap.get('extractionId'));

    this.vendorQuoteDataService.getDraft(this.bidId, this.draftId).subscribe(draft => {
      this.draft = draft && draft.bidId === this.bidId ? draft : undefined;
      this.isLoading = false;
      if (this.draft?.file.url) {
        if (this.draft.file.mime === 'application/pdf') {
          // Same approach as shared/pdf-preview: the browser's PDF viewer in an iframe.
          this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${this.draft.file.url}#toolbar=0&navpanes=0`);
        } else if (this.draft.file.mime.startsWith('image/')) {
          this.imageUrl = this.draft.file.url;
        }
      }
      this.suggestScopes();
    });

    this.backendApiService.getApi(`bids/${this.bidId}`).subscribe((response: any) => {
      this.bid = response?.data ?? null;
    });

    // D6: the bid's own scopes.
    this.backendApiService.getApi(`bids/get-scopes/${this.bidId}`, { per_page: 100, page: 1 }).subscribe((response: any) => {
      const rows: any[] = Array.isArray(response?.data) ? response.data : [];
      this.scopeOptions = rows.map(s => ({
        // `base_scope_id`, NOT `id`. This endpoint merges proposal-derived rows (where `id` is the
        // proposal scope's id) with bid-derived ones (where it is the scope's). Only
        // `base_scope_id` is reliably `scopes.id`, which is what the quote → scope pivot stores —
        // sending `id` made the server reject every proposal-derived scope as invalid.
        id: Number(s.base_scope_id ?? s.id),
        label: [s.scope_code, s.scope_name].filter(Boolean).join(' - '),
        // Set by suggestScopes() from the matched products' own scopes.
        suggested: false,
        matchCount: 0,
      }));
      this.scopesLoaded = true;
      this.suggestScopes();
    });
  }

  /** Lines matched to a product already in the catalog — the only ones with scopes to go by. */
  get matchedLineCount(): number {
    return this.draft?.lines.filter(l => l.matchedProduct).length ?? 0;
  }

  /**
   * Scopes from the matched items: a bid scope is suggested when at least one matched item's
   * product is in it. Runs once both the draft and the bid's scopes are in. Pre-selects them only
   * when no scope is chosen yet — a choice the user (or an earlier visit) made is never replaced.
   */
  private suggestScopes(): void {
    if (!this.draft || !this.scopesLoaded || this.scopeSuggestionDone) return;
    this.scopeSuggestionDone = true;

    const byProduct = this.draft.matchedProductScopes ?? {};
    const onBid = new Set(this.scopeOptions.map(o => o.id));
    const counts = new Map<number, number>();
    const offBid = new Map<string, number>();
    for (const line of this.draft.lines) {
      const scopes = line.matchedProduct ? byProduct[String(line.matchedProduct.id)] ?? [] : [];
      for (const s of scopes) {
        if (onBid.has(s.id)) {
          counts.set(s.id, (counts.get(s.id) ?? 0) + 1);
        } else {
          offBid.set(s.label, (offBid.get(s.label) ?? 0) + 1);
        }
      }
    }

    this.scopeOptions = this.scopeOptions.map(o => ({ ...o, matchCount: counts.get(o.id) ?? 0, suggested: counts.has(o.id) }));
    this.offBidScopes = [...offBid].map(([label, count]) => ({ label, count }));

    const suggested = [...new Set(this.scopeOptions.filter(o => o.suggested).map(o => o.id))];
    if (suggested.length && !this.draft.scopes.length) {
      this.draft.scopes = suggested;
      this.autoScopeIds = suggested;
      this.scopeError = false;
      this.persist();
    }
  }

  /** The auto-selected scopes the user has kept, with their match counts, for the note under the field. */
  get autoScopesKept(): ScopeOption[] {
    const chosen = new Set(this.draft?.scopes ?? []);
    const seen = new Set<number>();
    return this.scopeOptions.filter(o => this.autoScopeIds.includes(o.id) && chosen.has(o.id) && !seen.has(o.id) && seen.add(o.id));
  }

  ngOnDestroy(): void {
    this.newProductTip = null;
  }

  get bidLabel(): string {
    return this.bid?.bid_number ? this.bid.bid_number : `#${this.bidId}`;
  }

  /* ── Counts and totals (D3, D7, D8) ─────────────────────────────────── */

  /** Lines that go into the quote — credit / discount lines (rejected) are shown but left out. */
  get keptLines(): ExtractedLine[] {
    return this.draft?.lines.filter(l => !l.rejected) ?? [];
  }

  get creditCount(): number {
    return this.draft?.lines.filter(l => l.rejected).length ?? 0;
  }

  get flagCount(): number {
    return this.keptLines.filter(l => l.needsCheck).length;
  }

  get matchedCount(): number {
    return this.keptLines.filter(l => !!l.matchedProduct).length;
  }

  get newCount(): number {
    return this.keptLines.filter(l => !l.matchedProduct).length;
  }

  get materialsTotal(): number {
    return this.keptLines.reduce((t, l) => t + this.lineAmount(l), 0);
  }

  /** qty × unit price — our own figure for a line. */
  calcLineTotal(line: ExtractedLine): number {
    return (line.qty.value || 0) * (line.unitPrice.value || 0);
  }

  /** What the line counts as: the document's printed total when chosen, else qty × unit price. */
  lineAmount(line: ExtractedLine): number {
    return line.useDocTotal && line.lineTotal != null ? Number(line.lineTotal) : this.calcLineTotal(line);
  }

  /** Switch a line between our calculated total and the document's. The unit price is not changed. */
  toggleDocTotal(line: ExtractedLine): void {
    if (line.lineTotal == null) return;
    line.useDocTotal = !line.useDocTotal;
    this.persist();
  }

  /** Lines + freight — what "Calculated" uses, and what the lines are checked against. */
  get calculatedTotal(): number {
    return this.materialsTotal + (this.draft?.freight || 0);
  }

  get totalSource(): 'calculated' | 'document' | 'manual' {
    return this.draft?.quoteTotalSource ?? 'calculated';
  }

  /** The total the quote saves with: calculated, the document's own, or the user's figure. */
  get quoteTotal(): number {
    if (this.totalSource === 'document' && this.documentQuoteTotal != null) return this.documentQuoteTotal;
    if (this.totalSource === 'manual') return Number(this.draft?.quoteTotal) || 0;
    return this.calculatedTotal;
  }

  /** Document / Custom fix the total, so the lines can drift from it: lines + freight − total. */
  get linesGap(): number {
    return this.calculatedTotal - this.quoteTotal;
  }

  setTotalSource(source: 'calculated' | 'document' | 'manual'): void {
    if (!this.draft || source === this.totalSource) return;
    if (source === 'document' && this.documentQuoteTotal == null) return;
    // Custom starts from the figure on screen, so switching never jumps to $0.
    if (source === 'manual') this.draft.quoteTotal = Math.round(this.quoteTotal * 100) / 100;
    this.draft.quoteTotalSource = source;
    this.persist();
  }

  onQuoteTotalInput(raw: string): void {
    if (!this.draft) return;
    this.draft.quoteTotal = Math.max(0, Math.round((parseFloat(raw) || 0) * 100) / 100);
    this.persist();
  }

  /** Sum of the line totals printed on the document, or null when no line has one. */
  get documentLinesTotal(): number | null {
    const printed = (this.draft?.lines ?? []).filter(l => l.lineTotal != null);
    return printed.length ? printed.reduce((t, l) => t + Number(l.lineTotal), 0) : null;
  }

  /** The quote total printed on the document, or null when the reader did not find one. */
  get documentQuoteTotal(): number | null {
    return this.draft?.documentTotal != null ? Number(this.draft.documentTotal) : null;
  }

  /** What our total is checked against: the document's quote total, else the sum of its line totals. */
  get documentCheckTotal(): number | null {
    if (this.documentQuoteTotal != null) {
      return this.documentQuoteTotal;
    }
    const lines = this.documentLinesTotal;
    return lines != null ? lines + (this.draft?.freight || 0) : null;
  }

  /** Our quote total minus the document's (0 when the document has none). */
  get documentDifference(): number {
    const doc = this.documentCheckTotal;
    return doc != null ? this.calculatedTotal - doc : 0;
  }

  get totalMatches(): boolean {
    return this.documentCheckTotal != null && Math.abs(this.documentDifference) < 0.005;
  }

  /** The document prints a different total for this line than qty × unit price gives. */
  lineDiffers(line: ExtractedLine): boolean {
    return line.lineTotal != null && Math.abs(this.calcLineTotal(line) - Number(line.lineTotal)) >= 0.005;
  }

  /* ── Same-name new products ─────────────────────────────────────────── */

  /**
   * Line numbers (1-based) of the OTHER new-product lines carrying this line's name. Each unmatched
   * line becomes its own catalogue product on save, so two with one name (a Lyon quote's 3-wide and
   * 1-wide lockers both read as "Standard Single Tier Locker - 12"w x 12"d x 72"h") would be two
   * products nobody can tell apart. Matched lines are left out: they create nothing.
   */
  sameNameAs(line: ExtractedLine): number[] {
    if (!this.draft || line.matchedProduct || line.rejected) return [];
    const key = VendorQuoteReviewComponent.nameKey(line.name);
    const out: number[] = [];
    this.draft.lines.forEach((other, k) => {
      if (other !== line && !other.matchedProduct && !other.rejected && VendorQuoteReviewComponent.nameKey(other.name) === key) out.push(k + 1);
    });
    return out;
  }

  /** How many new-product lines share a name with another one. */
  get sameNameCount(): number {
    return this.draft?.lines.filter(l => this.sameNameAs(l).length > 0).length ?? 0;
  }

  private static nameKey(name: string): string {
    return (name || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  /* ── Editing (D2, D7, D9) ───────────────────────────────────────────── */

  toggleEdit(): void {
    this.editing = !this.editing;
  }

  /** ✎ when the user changed it; back at the original value it gets its read source again (✦ or calculated). */
  onFieldChange(field: ExtractedField<any>): void {
    field.source = String(field.value) === String(field.original) ? (field.basis ? 'calculated' : 'document') : 'manual';
    this.persist();
  }

  onQtyChange(line: ExtractedLine, raw: string | number): void {
    line.qty.value = Math.max(0, parseInt(String(raw), 10) || 0);
    this.onFieldChange(line.qty);
  }

  onPriceChange(line: ExtractedLine, raw: string | number): void {
    line.unitPrice.value = Math.max(0, parseFloat(String(raw)) || 0);
    // Fixing the flagged price clears "Check this".
    line.needsCheck = false;
    this.onFieldChange(line.unitPrice);
  }

  /** A new product's name — what it will be called in the catalogue. A blank entry keeps the old name. */
  onNameChange(line: ExtractedLine, raw: string): void {
    const name = (raw || '').trim().replace(/\s+/g, ' ');
    if (!name || name === line.name) return;
    line.name = name;
    this.persist();
  }

  sourceTooltip(field: ExtractedField<any>): string {
    if (field.source === 'manual') return 'Manually entered';
    if (field.source === 'calculated') return this.calcTooltip(field);
    return 'Read from document';
  }

  /** "Calculated by us, not printed on the document. Line total $97.34 ÷ 156 = $0.62." */
  calcTooltip(field: ExtractedField<any>): string {
    const shown = typeof field.original === 'number' && field.basis?.includes('÷')
      ? ` = ${field.original.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`
      : '';
    return `Calculated by us, not taken from the document. ${field.basis ?? ''}${shown}.`;
  }

  onScopesChange(): void {
    if (this.draft?.scopes.length) {
      this.scopeError = false;
    }
    this.persist();
  }

  private persist(): void {
    // In the dialog, edits go to the server only for a real draft (a PO upload); never for a local one.
    if (this.draft && (!this.inDialog || this.draft.id > 0)) {
      this.vendorQuoteDataService.updateDraft(this.draft).subscribe();
    }
  }

  /* ── NEW PRODUCT tooltip (D7) ───────────────────────────────────────── */

  showNewProductTip(line: ExtractedLine, event: Event): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 340;
    const x = Math.min(Math.max(8, rect.left + rect.width / 2 - width / 2), window.innerWidth - width - 8);
    const below = rect.bottom + 8;
    const y = below + 260 > window.innerHeight - 8 ? Math.max(8, rect.top - 268) : below;
    this.newProductTip = { line, x, y };
  }

  hideNewProductTip(): void {
    this.newProductTip = null;
  }

  /** Name searched in the catalog = the line name without its leading product code. */
  searchedName(line: ExtractedLine): string {
    return line.name.replace(line.code, '').trim().toLowerCase();
  }

  /* ── Cancel / Save (D2, D10) ────────────────────────────────────────── */

  /** Cancel deletes the draft, so ask first — nothing is removed unless the user confirms. */
  cancelDraft(): void {
    if (this.isSaving) return;
    // Dialog: nothing has been saved as a quote, so closing needs no confirmation. A real draft (a PO
    // upload) is deleted so it doesn't linger in the bid's unfinished uploads.
    if (this.inDialog) {
      if (this.draft && this.draft.id > 0) {
        this.vendorQuoteDataService.deleteDraft(this.draft.bidId, this.draft.id).subscribe();
      }
      this.dialogRef!.close(undefined);
      return;
    }
    const name = this.draft?.file.name;
    const vendor = this.draft?.vendor?.name?.value;
    // A new vendor is only created when the quote is SAVED, so discarding means it is not added at all.
    const vendorNote = this.draft?.vendorIsNew
      ? `The new vendor${vendor ? ' ' + vendor : ''} is not added either: it is only added when the quote is saved.`
      : `Your vendor${vendor ? ' ' + vendor : ''} is not changed.`;
    const dialogRef = this.dialog.open(ConfirmationPopupComponent, {
      width: '520px',
      // Blue theme header, like the other vendor-quote popups.
      panelClass: 'vq-themed-popup',
      data: {
        title: 'Discard this quote?',
        titleIcon: 'warning',
        isWarning: true,
        messageParts: name
          ? [{ text: 'The quote read from ' }, { text: name, bold: true }, { text: ' has not been saved. If you cancel, this draft and any changes you made are deleted.' }]
          : [{ text: 'This quote has not been saved. If you cancel, this draft and any changes you made are deleted.' }],
        description: `${vendorNote} To keep the quote, choose Keep Reviewing and then Save Quote.`,
        confirmButtonText: 'Discard Quote',
        confirmIcon: 'delete',
        cancelButtonText: 'Keep Reviewing',
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.vendorQuoteDataService.deleteDraft(this.bidId, this.draftId).subscribe(() => {
        this.toasterService.showSuccessMessage('Draft deleted.');
        this.backToVendorQuotes();
      });
    });
  }

  saveQuote(): void {
    if (!this.draft || this.isSaving) return;
    // Scopes belong to a bid; the dialog has none to choose.
    if (!this.inDialog && !this.draft.scopes.length) {
      this.scopeError = true;
      document.getElementById('rv-scopes')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.toasterService.showError('Choose the scope(s) this quote is for, then save.');
      return;
    }
    if (this.sameNameCount) {
      const first = this.draft.lines.findIndex(l => this.sameNameAs(l).length > 0);
      this.editing = true;
      document.getElementById('rv-line-' + first)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.toasterService.showError('Some new products have the same name. Rename them so you can tell them apart, then save.');
      return;
    }
    // Dialog: on to Compare with the reviewed draft.
    if (this.inDialog) {
      this.dialogRef!.close({ draft: this.draft });
      return;
    }
    this.isSaving = true;
    const code = this.draft.quoteNumber.value;
    const count = this.keptLines.length;
    const scopes = this.draft.scopes
      .map(id => this.scopeOptions.find(o => o.id === id)?.label ?? id)
      .join(', ');
    this.draft.lines.forEach(l => (l.needsCheck = false));
    this.saveReviewedDraft(code, count, scopes);
  }

  /**
   * Save the reviewed draft, asking first when this bid already holds a quote with this number.
   *
   * `force` is what the buyer chose after being shown the one already there. Without it the
   * server answers 409 rather than creating a second copy of the same quote.
   */
  private saveReviewedDraft(code: string, count: number, scopes: string, force = false): void {
    this.vendorQuoteDataService.saveDraft(this.bidId, this.draftId, force).subscribe({
      next: () => {
        this.toasterService.showSuccessMessage(`${code} saved with ${count} material${count === 1 ? '' : 's'} to ${scopes}.`);
        this.backToVendorQuotes();
      },
      error: (err: any) => {
        this.isSaving = false;
        // 409: a quote with this number is already here. Retrying cannot help, so asking what
        // the buyer wants is the only sensible answer — "try again" would have been a lie.
        if (err?.duplicate) { this.confirmDuplicateQuote(err, code, count, scopes); return; }

        this.toasterService.showError('The quote could not be saved. Try again.');
      },
    });
  }

  /** "You already have this quote — save a second copy, or go back to the one that is there?" */
  private confirmDuplicateQuote(err: any, code: string, count: number, scopes: string): void {
    this.dialog.open(ConfirmationPopupComponent, {
      width: '560px',
      panelClass: 'custom-dialog',
      data: {
        ...duplicateQuoteDialogData(err, 'bid', code),
        confirmButtonText: 'Save a copy',
        cancelButtonText: 'Keep existing',
      },
    }).afterClosed().subscribe((saveAnyway: boolean) => {
      if (!saveAnyway) { return; }
      this.isSaving = true;
      this.saveReviewedDraft(code, count, scopes, true);
    });
  }

  backToVendorQuotes(): void {
    this.router.navigate(['/bids/bid', this.bidId], { queryParams: { tab: BID_VENDOR_QUOTES_TAB } });
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(`${value}T12:00:00`);
    if (isNaN(d.getTime())) return value;
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  }

  trackByLine(index: number, line: ExtractedLine): number {
    return line.lineNo;
  }
}
